import React, { useState, useEffect, useRef, useMemo } from 'react';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import ValidationErrorModalComponent from '../../components/ValidationErrorModalComponent';
import { RecruitmentService } from '../../service/RecruitmentService';
import GeneralUtils from '../../utilities/GeneralUtils';

const RecruitmentPlanInstitutionEdit = (props) => {
    let emptyRecruitmentPlan = {
        id: null,
        recruitmentCampaign: { id: null, name: '' },
        institution: { id: null, name: '' },
        responsibleName: '',
        responsiblePosition: '',
        responsiblePhone: '',
        responsibleEmail: '',
    };

    const [recruitmentPlan, setRecruitmentPlan] = useState(null);
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);

    const recruitmentService = useMemo(() => new RecruitmentService(), []);

    const toast = useRef(null);

    useEffect(() => {
        let _recruitmentPlan = GeneralUtils.emptyInNullForObjectFields(props?.value);
        setRecruitmentPlan(_recruitmentPlan);

        // console.log("_recruitmentPlan: ", _recruitmentPlan)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props, recruitmentService]);

    const hideDialog = () => {
        if (typeof props.visibleSetter === 'function') {
            props.visibleSetter(false);
        }
        formik.resetForm();
    };

    const saveDetails = (data) => {
        recruitmentService.saveRecruitmentPlan(data).then(
            (response) => {
                if (typeof props.afterSave === 'function') {
                    props.afterSave(response.recruitmentPlan);
                }                
                hideDialog()
            },
            (error) => {
                // console.log(error)
                if (error?.hasValidationError) {
                    setValidationErrors(error.errorCodes)
                    setShowValidationErrorDialog(true)
                } else {
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
                }
            }
        );
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: recruitmentPlan ? recruitmentPlan : emptyRecruitmentPlan,
        validate: (data) => {
            let errors = {};

            if (!data.responsibleName || !data.responsibleName.trim()) {
                errors.responsibleName = translatedMessage("form.error.institution.responsibleName.required");
            }

            if (!data.responsiblePosition || !data.responsiblePosition.trim()) {
                errors.responsiblePosition = translatedMessage("form.error.institution.responsiblePosition.required");
            }

            if (!data.responsiblePhone || !data.responsiblePhone.trim()) {
                errors.responsiblePhone = translatedMessage("form.error.institution.responsiblePhone.required");
            }

            if (!data.responsibleEmail || !data.responsibleEmail.trim()) {
                errors.responsibleEmail = translatedMessage("form.error.institution.responsibleEmail.required");
            }
            else if (!/^[A-Z0-9._%+-]+@[A-Z0-9._-]+\.[A-Z]{2,100}$/i.test(data.responsibleEmail)) {
                errors.responsibleEmail = translatedMessage("form.error.institution.responsibleEmail.invalid");
            }

            return errors;
        },
        onSubmit: (data) => {
            // console.log("save institution details: ", data)
            saveDetails(data);
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const dialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={translatedMessage('generic.save')} icon="pi pi-check" className="p-button p-button-primary" form="institutionDetails-form" type="submit" />
        </>
    );

    return (
        <>
            <Toast ref={toast} />

            <Dialog
                visible={props?.visible}
                header={translatedMessage('institution.edit')}
                modal
                resizable={false}
                className="pcn-dialog p-fluid"
                footer={dialogFooter}
                onHide={hideDialog}
            >
                <form id="institutionDetails-form" onSubmit={formik.handleSubmit}>
                    <div className="field p-inputgroup mt-3 mb-2">
                        <span className="p-float-label">
                            <InputText type="text" name="responsibleName" id="responsibleName"
                                value={formik.values.responsibleName} onChange={formik.handleChange}
                                className={classNames({ 'p-invalid': isFormFieldValid('responsibleName') })} />
                            <label htmlFor="responsibleName" className={classNames({ 'p-error': isFormFieldValid('responsibleName') })}>
                                {translatedMessage("recruitment.plan.responsibleName")} *
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('responsibleName')}

                    <div className="field p-inputgroup mt-3 mb-2">
                        <span className="p-float-label">
                            <InputText type="text" name="responsiblePosition" id="responsiblePosition"
                                value={formik.values.responsiblePosition} onChange={formik.handleChange}
                                className={classNames({ 'p-invalid': isFormFieldValid('responsiblePosition') })} />
                            <label htmlFor="responsiblePosition" className={classNames({ 'p-error': isFormFieldValid('responsiblePosition') })}>
                                {translatedMessage("recruitment.plan.responsiblePosition")} *
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('responsiblePosition')}

                    <div className="field p-inputgroup mt-3 mb-2">
                        <span className="p-float-label">
                            <InputText type="text" name="responsiblePhone" id="responsiblePhone"
                                value={formik.values.responsiblePhone} onChange={formik.handleChange} 
                                className={classNames({ 'p-invalid': isFormFieldValid('responsiblePhone') })} keyfilter="int" maxLength={10}
                                tooltip={translatedMessage("profile.phone.format")} tooltipOptions={{ position: 'top' }} />                            
                            <label htmlFor="responsiblePhone" className={classNames({ 'p-error': isFormFieldValid('responsiblePhone') })}>
                                {translatedMessage("recruitment.plan.responsiblePhone")} *
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('responsiblePhone')}

                    <div className="field p-inputgroup mt-3 mb-2">
                        <span className="p-float-label">
                            <InputText type="text" name="responsibleEmail" id="responsibleEmail"
                                value={formik.values.responsibleEmail} onChange={formik.handleChange} 
                                className={classNames({ 'p-invalid': isFormFieldValid('responsibleEmail') })} />                            
                            <label htmlFor="responsibleEmail" className={classNames({ 'p-error': isFormFieldValid('responsibleEmail') })}>
                                {translatedMessage("recruitment.plan.responsibleEmail")} *
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('responsibleEmail')}

                </form>
            </Dialog>

            {showValidationErrorDialog &&
                <ValidationErrorModalComponent
                    visible={showValidationErrorDialog}
                    errors={validationErrors}
                    dialogTitle={translatedMessage("recruitment.plan.save.error.title")}
                    closeDialog={() => setShowValidationErrorDialog(false)}
                />
            }
        </>
    );
};

export default RecruitmentPlanInstitutionEdit;
